import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../utils/api';

const PaymentContext = createContext();


export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const [payments, setPayments] = useState([]);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [orderSummary, setOrderSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // PHASE-3 · STALE RESPONSES ARE REJECTED WHERE THE STATE IS WRITTEN.
  // The payment page guarded its own derived eligibility, but `currentPayment`
  // and `orderSummary` are written HERE: a slow answer for the collection the
  // patient had just navigated away from still landed, and the page then showed
  // the previous order's amount and currency over the current one. Each writer
  // takes a ticket; only the newest ticket for that surface may publish — the
  // response is still returned to its own caller, which keeps its own guard.
  const paymentTicket = useRef(0);
  const summaryTicket = useRef(0);

  const getAuthHeaders = useCallback(() => {
    const token = '';
    return {
      'Content-Type': 'application/json',
      
    };
  }, []);

  const fetchPatientPayments = useCallback(async (patientId, limit = 50) => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/payments/patient/${patientId}?limit=${limit}`);
      const data = res.data;
      setPayments(data.payments || []);
      return data.payments || [];
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchOrderPayments = useCallback(async (orderId) => {
    if (!orderId) return null;
    setLoading(true);
    try {
      const res = await api.get(`/payments/order/${orderId}`);
      return res.data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const clearError = useCallback(() => setError(null), []);

  const fetchOrderSummary = useCallback(async (orderId) => {
    if (!orderId) return null;
    const ticket = ++summaryTicket.current;
    setLoading(true);
    try {
      const res = await api.get(`/payments/order/${orderId}/summary`);
      const data = res.data;
      if (ticket === summaryTicket.current) setOrderSummary(data);
      return data;
    } catch (err) {
      if (ticket === summaryTicket.current) setError(err.message);
      return null;
    } finally {
      if (ticket === summaryTicket.current) setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchPayment = useCallback(async (paymentId) => {
    if (!paymentId) return null;
    const ticket = ++paymentTicket.current;
    setLoading(true);
    try {
      const res = await api.get(`/payments/${paymentId}`);
      const data = res.data;
      if (ticket === paymentTicket.current) setCurrentPayment(data);
      return data;
    } catch (err) {
      if (ticket === paymentTicket.current) setError(err.message);
      return null;
    } finally {
      if (ticket === paymentTicket.current) setLoading(false);
    }
  }, [getAuthHeaders]);

  // FE-WIRING-2 / TASK F: removed dead-code shadow surfaces from this
  // context. The following methods were exposed but had ZERO callers
  // across the codebase and would have enabled R-7 forbidden-surface
  // access (commission internals, refund internals, payment-attempts
  // internals). They are intentionally NOT re-exposed:
  //   - createPayment           (POST /payments)         — payment mutation
  //   - fetchRefundsForPayment  (GET /refunds/payment/*) — R-7 forbidden
  //   - fetchPaymentAttempts    (GET /payment-attempts/payment/*) — R-7 forbidden
  //   - resolveCommission       (POST /commission/resolve)        — R-7 forbidden
  //
  // The Stripe checkout helpers below remain — they back the existing
  // patient checkout flow which is OUT OF SCOPE for FE-WIRING-2 (no
  // checkout work; do not start PSP). They are NOT new surfaces.

  const initiateCheckout = useCallback(async (paymentId) => {
    setLoading(true);
    setError(null);
    try {
      const originUrl = window.location.origin;
      const res = await api.post(`/payments/${paymentId}/checkout`, { origin_url: originUrl, return_path: '/patient/payment' });
      const data = res.data;
      return data;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const pollCheckoutStatus = useCallback(async (paymentId) => {
    const ticket = ++paymentTicket.current;
    try {
      const res = await api.get(`/payments/${paymentId}/checkout-status`);
      const data = res.data;
      // Shares the payment ticket: a late poll for a previous collection must
      // not overwrite the payment the patient is looking at now.
      if (data.payment && ticket === paymentTicket.current) {
        setCurrentPayment(data.payment);
      }
      return data;
    } catch (err) {
      if (ticket === paymentTicket.current) setError(err.message);
      return null;
    }
  }, [getAuthHeaders]);

  // A consumer that establishes a new payment context (a different order or
  // collection) invalidates every in-flight answer for the previous one.
  const resetPaymentContext = useCallback(() => {
    paymentTicket.current += 1;
    summaryTicket.current += 1;
    setCurrentPayment(null);
    setOrderSummary(null);
  }, []);

  const checkPaymentEligibility = useCallback(async (orderId, quoteId) => {
    try {
      const params = new URLSearchParams();
      if (orderId) params.set('order_id', orderId);
      if (quoteId) params.set('quote_id', quoteId);
      const res = await api.get(`/patient/payment/eligibility?${params}`);
      return res.data;
    } catch (err) {
      // PHASE 2B/B1 — surface the backend fail-closed code (financial_contract_missing,
      // payment_obligation_unavailable, authoritative_currency_missing) verbatim; the
      // page localizes it. Never fabricate an amount/currency client-side.
      const data = err?.response?.data || {};
      return {
        eligible: false, payment_enabled: false,
        // POST-PAYMENT-TRUTH-02 — a transport/5xx failure is NOT a financial
        // statement. `eligibility_unavailable` lets the page say "we could not
        // check" with a retry instead of "nothing payable right now".
        eligibility_unavailable: !data.code,
        http_status: err?.response?.status || null,
        code: data.code || null,
        reason: data.message || data.detail || 'Unable to check eligibility',
      };
    }
  }, [getAuthHeaders]);

  const value = {
    payments,
    currentPayment,
    orderSummary,
    loading,
    error,
    fetchPatientPayments,
    fetchOrderPayments,
    fetchOrderSummary,
    fetchPayment,
    initiateCheckout,
    pollCheckoutStatus,
    checkPaymentEligibility,
    setCurrentPayment,
    resetPaymentContext,
    // PHASE-2 · STABLE IDENTITY. This was an inline arrow, so a NEW function was
    // handed out on every provider render. Consumers that (correctly) list it in
    // a `useCallback`/`useEffect` dependency chain then re-created their loader
    // on every render, which on the payment page meant an unbounded request
    // loop. `useCallback` with no dependencies keeps it referentially stable,
    // so the dependency can be declared honestly instead of silenced with an
    // eslint-disable.
    clearError,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentContext;
